import { useNavigate } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'

interface LogoutButtonProps {
  className?: string
  showText?: boolean
  variant?: 'default' | 'header' | 'sidebar'
  onLogoutComplete?: () => void
}

export default function LogoutButton({ 
  className = '',
  showText = true,
  variant = 'default',
  onLogoutComplete
}: LogoutButtonProps) {
  const navigate = useNavigate()
  const { isAuthenticated, logout, isLoading } = useAuth()

  // Only show the button when user is authenticated (Requirement 5.1)
  if (!isAuthenticated) {
    return null
  }

  const handleLogout = async () => {
    try {
      // Handle logout action (Requirement 5.2)
      await logout() // This clears local storage (Requirement 5.3)
      
      // Call optional callback
      if (onLogoutComplete) {
        onLogoutComplete()
      }
      
      // Redirect to login page (Requirement 5.4)
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
      // Even if logout API fails, still redirect to login
      navigate('/login')
    }
  }

  // Get variant-specific styles
  const getVariantStyles = () => {
    switch (variant) {
      case 'header':
        return 'flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors'
      case 'sidebar':
        return 'flex items-center space-x-3 w-full px-3 py-2 text-left text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-md transition-colors'
      default:
        return 'inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors'
    }
  }

  const buttonStyles = `${getVariantStyles()} ${className}`

  return (
    <button
      onClick={handleLogout}
      disabled={isLoading}
      className={buttonStyles}
      aria-label="Logout"
    >
      <LogOut className="h-4 w-4" />
      {showText && (
        <span className={variant === 'header' ? 'hidden sm:inline' : ''}>
          {isLoading ? 'Logging out...' : 'Logout'}
        </span>
      )}
    </button>
  )
}